import React, { useState, useEffect, createContext } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import CustomerDashboard from './CustomerDashboard';
import CustomerList from './CustomerList';
import CustomerProfile from './CustomerProfile';
import Login from './Login';
import PrivateRoute from './PrivateRoute';
import logo from './midflorida-logo.svg';
import { authFetch, logout, fetchCsrfToken } from './authFetch';
import AdminImports from './AdminImports';
import AdminPDFTemplates from './AdminPDFTemplates';
import AdminCommissions from './AdminCommissions';
import CalendarView from './CalendarView';
import MyProfile from './MyProfile';
import HistoricalFiles from './HistoricalFiles';
import CallRecordings from './CallRecordings';
import AgentAlerts from './AgentAlerts';
import AdminLeadImport from './AdminLeadImport';
import AdminAgentReport from './AdminAgentReport';
import {
  Home, Users, Calendar, Bell, Archive, User, Upload, FileText,
  DollarSign, Phone, UserPlus, BarChart3, Menu, X, ChevronLeft, LogOut
} from 'lucide-react';
import IdleTimeout from './IdleTimeout';
import PublicFormSigning from './PublicFormSigning';
import OAuthCallback from './OAuthCallback';
import Debug from './Debug';

const DEBUG_MODE = false;

export const UserContext = createContext(null);

function DebugRoute() {
  if (!DEBUG_MODE) return <Navigate to="/" />;
  return (
    <div className="max-w-4xl mx-auto mt-8 p-8">
      <div className="bg-yellow-50 border border-yellow-300 rounded p-4 mb-4">
        <h2 className="text-xl font-bold text-yellow-800 mb-2">Debug Mode</h2>
        <p className="text-sm text-gray-700">Debug tools are active.</p>
      </div>
      <Debug />
    </div>
  );
}

// Sidebar Navigation Component
function Sidebar({ isAdmin, collapsed, setCollapsed, currentUser, handleLogout }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const isActive = (path) => {
    if (path === '/') return currentPath === '/';
    return currentPath.startsWith(path);
  };

  const linkClass = (path) => {
    const base = 'flex items-center px-3 py-2 rounded-lg transition-colors text-sm';
    if (isActive(path)) {
      return `${base} bg-blue-700 text-white`;
    }
    return `${base} text-gray-300 hover:bg-blue-700/50 hover:text-white`;
  };

  return (
    <aside
      className={`fixed top-0 left-0 h-full bg-blue-900 text-white z-40 transition-all duration-200 flex flex-col ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Top: collapse toggle */}
      <div className="flex items-center justify-between p-3 border-b border-blue-800">
        {!collapsed && (
          <Link to="/" className="flex-1">
            <img src={logo} alt="MFA" style={{ height: '40px' }} />
          </Link>
        )}
        <button
          onClick={() => {
            setCollapsed(!collapsed);
            localStorage.setItem('sidebarCollapsed', !collapsed);
          }}
          className="p-1 rounded hover:bg-blue-800 text-gray-300"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <Menu className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>

      {/* Navigation links */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
        {/* Main section */}
        {!collapsed && <p className="text-xs text-blue-400 uppercase tracking-wider px-3 mb-1">Main</p>}
        <Link to="/" className={linkClass('/')} title="Dashboard">
          <Home className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="ml-3">Dashboard</span>}
        </Link>
        <Link to="/customers" className={linkClass('/customers')} title="Customers">
          <Users className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="ml-3">Customers</span>}
        </Link>
        <Link to="/calendar" className={linkClass('/calendar')} title="Calendar">
          <Calendar className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="ml-3">Calendar</span>}
        </Link>
        <Link to="/alerts" className={linkClass('/alerts')} title="Alerts">
          <Bell className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="ml-3">Alerts</span>}
        </Link>
        <Link to="/historical-files" className={linkClass('/historical-files')} title="Historical Files">
          <Archive className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="ml-3">Historical Files</span>}
        </Link>

        {/* My Account */}
        {!collapsed && <p className="text-xs text-blue-400 uppercase tracking-wider px-3 mt-4 mb-1">My Account</p>}
        {collapsed && <hr className="border-blue-800 my-2" />}
        <Link to="/profile" className={linkClass('/profile')} title="Profile">
          <User className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="ml-3">Profile</span>}
        </Link>

        {/* Admin section */}
        {isAdmin && (
          <>
            {!collapsed && <p className="text-xs text-blue-400 uppercase tracking-wider px-3 mt-4 mb-1">Admin</p>}
            {collapsed && <hr className="border-blue-800 my-2" />}
            <Link to="/admin/imports" className={linkClass('/admin/imports')} title="FL Blue Imports">
              <Upload className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="ml-3">FL Blue Imports</span>}
            </Link>
            <Link to="/admin/pdf-templates" className={linkClass('/admin/pdf-templates')} title="PDF Templates">
              <FileText className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="ml-3">PDF Templates</span>}
            </Link>
            <Link to="/admin/commissions" className={linkClass('/admin/commissions')} title="Commissions">
              <DollarSign className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="ml-3">Commissions</span>}
            </Link>
            <Link to="/admin/call-recordings" className={linkClass('/admin/call-recordings')} title="Recordings">
              <Phone className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="ml-3">Recordings</span>}
            </Link>
            <Link to="/admin/lead-import" className={linkClass('/admin/lead-import')} title="Lead Import">
              <UserPlus className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="ml-3">Lead Import</span>}
            </Link>
            <Link to="/admin/agent-report" className={linkClass('/admin/agent-report')} title="Agent Report">
              <BarChart3 className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="ml-3">Agent Report</span>}
            </Link>
          </>
        )}
      </nav>

      {/* Bottom: user info + logout */}
      <div className="border-t border-blue-800 p-3">
        {!collapsed && currentUser && (
          <p className="text-xs text-blue-300 truncate mb-2">{currentUser.username}</p>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center w-full px-3 py-2 rounded-lg text-sm text-gray-300 hover:bg-red-700/60 hover:text-white transition-colors"
          title="Logout"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="ml-3">Logout</span>}
        </button>
      </div>
    </aside>
  );
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  const isAdmin = currentUser?.is_staff || (currentUser?.groups || []).some(g => (g.name || '').toLowerCase() === 'admin');

  useEffect(() => {
    const checkUser = async () => {
      try {
        const BASE_URL = process.env.REACT_APP_API_BASE_URL;
        await fetchCsrfToken();
        const response = await authFetch(`${BASE_URL}/api/auth/user/`);
        if (response.ok) {
          const userData = await response.json();
          const userDetailResponse = await authFetch(`${BASE_URL}/api/auth/debug/`);
          if (userDetailResponse.ok) {
            const userDetails = await userDetailResponse.json();
            const enrichedUserData = {
              ...userData,
              id: userDetails.id,
              groups: userDetails.groups,
              is_staff: userDetails.is_staff,
              is_superuser: userDetails.is_superuser
            };
            console.log("Enriched user data:", enrichedUserData);
            localStorage.setItem('userDetails', JSON.stringify(enrichedUserData));
            setCurrentUser(enrichedUserData);
          } else {
            setCurrentUser(userData);
          }
        }
      } catch (error) {
        console.error("Error checking user:", error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setCurrentUser(null);
      localStorage.removeItem('userDetails');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  // Pages that don't use the sidebar layout
  const showSidebar = currentUser;
  const sidebarWidth = sidebarCollapsed ? 'pl-16' : 'pl-60';

  return (
    <UserContext.Provider value={{ currentUser, setCurrentUser }}>
      <IdleTimeout isAuthenticated={!!currentUser}>
        <div className="App min-h-screen bg-gray-100">
          {/* Sidebar - only when authenticated */}
          {showSidebar && (
            <Sidebar
              isAdmin={isAdmin}
              collapsed={sidebarCollapsed}
              setCollapsed={setSidebarCollapsed}
              currentUser={currentUser}
              handleLogout={handleLogout}
            />
          )}

          {/* Header */}
          <header className={`bg-blue-800 text-white p-3 flex justify-center items-center ${showSidebar ? sidebarWidth : ''} transition-all duration-200`}>
            <Link to="/">
              <img src={logo} alt="Mid Florida Agencies Logo" style={{ height: '60px' }} />
            </Link>
          </header>

          {/* Main content area */}
          <main className={`p-4 ${showSidebar ? sidebarWidth : ''} transition-all duration-200`}>
            <div className="max-w-7xl mx-auto">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/oauth/success" element={<OAuthCallback />} />

                <Route path="/" element={<PrivateRoute><CustomerDashboard /></PrivateRoute>} />
                <Route path="/customers" element={<PrivateRoute><CustomerList /></PrivateRoute>} />
                <Route path="/customers/:id" element={<PrivateRoute><CustomerProfile /></PrivateRoute>} />
                <Route path="/calendar" element={<PrivateRoute><CalendarView /></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><MyProfile /></PrivateRoute>} />
                <Route path="/historical-files" element={<PrivateRoute><HistoricalFiles /></PrivateRoute>} />
                <Route path="/alerts" element={<PrivateRoute><AgentAlerts /></PrivateRoute>} />

                {/* Admin Routes */}
                <Route path="/admin/imports" element={<PrivateRoute><AdminImports /></PrivateRoute>} />
                <Route path="/admin/pdf-templates" element={<PrivateRoute><AdminPDFTemplates /></PrivateRoute>} />
                <Route path="/admin/commissions" element={<PrivateRoute><AdminCommissions /></PrivateRoute>} />
                <Route path="/admin/call-recordings" element={<PrivateRoute><CallRecordings /></PrivateRoute>} />
                <Route path="/admin/lead-import" element={<PrivateRoute><AdminLeadImport /></PrivateRoute>} />
                <Route path="/admin/agent-report" element={<PrivateRoute><AdminAgentReport /></PrivateRoute>} />

                {/* Public Form Signing Route (no auth required) */}
                <Route path="/sign/:token" element={<PublicFormSigning />} />

                <Route path="/debug" element={<PrivateRoute><DebugRoute /></PrivateRoute>} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </div>
          </main>
        </div>
      </IdleTimeout>
    </UserContext.Provider>
  );
}

export default App;
